import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePet, MOOD_COPY, TIER_COLORS } from "./usePet";
import { toast } from "sonner";

/**
 * PhoenixPetWidget — floating top-right Tamagotchi badge that follows the
 * user across all pages. Click to open a quick-view panel with feed actions
 * and a link to the full /pet page.
 *
 * Hidden:
 *   - When user is logged out
 *   - On the /pet page itself (would be redundant)
 *   - When dismissed via localStorage flag `omni:pet-widget-hidden`
 */
export default function PhoenixPetWidget() {
  const { data, loading, reload } = usePet();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(typeof window !== "undefined" && localStorage.getItem("omni:pet-widget-hidden") === "1");
    // Hide on the /pet page itself
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/pet")) {
      setHidden(true);
    }
  }, []);

  if (hidden || loading || !data?.pet) return null;
  const pet = data.pet;
  const mood = MOOD_COPY[pet.mood] || MOOD_COPY.happy;
  const tierStyle = TIER_COLORS[pet.tier] || TIER_COLORS.egg;
  const xpPct = Math.min(100, (pet.xp / Math.max(1, pet.xp_cap)) * 100);

  const dismissForever = () => {
    if (window.confirm("Hide the pet widget? You can still visit /pet directly.")) {
      localStorage.setItem("omni:pet-widget-hidden", "1");
      setHidden(true);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="pet-widget-button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full bg-[#0F0F0F] border-2 ${tierStyle.ring} ${tierStyle.glow} flex items-center justify-center hover:scale-110 transition-transform`}
        title={`${pet.name} · ${pet.tier_label} · ${mood.label}`}
      >
        <span className="text-3xl leading-none">{pet.alive ? pet.icon : "💀"}</span>
        <span className="absolute -bottom-1 -right-1 text-base leading-none">{mood.face}</span>
      </button>

      {/* Slide-in panel */}
      {open && (
        <div
          data-testid="pet-widget-panel"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-[#0A0A0A] border-2 border-[#D4AF37] w-full max-w-sm p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">Your Phoenix</div>
                <div className="font-display text-2xl mt-1">{pet.name}</div>
                <div className="text-xs text-neutral-400">{pet.tier_label}</div>
              </div>
              <button data-testid="pet-widget-close" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="flex items-center gap-4 my-4">
              <div className={`w-20 h-20 rounded-full bg-[#0F0F0F] border-2 ${tierStyle.ring} ${tierStyle.glow} flex items-center justify-center`}>
                <span className="text-5xl">{pet.alive ? pet.icon : "💀"}</span>
              </div>
              <div className="flex-1">
                <div className="text-xs text-neutral-400 mb-1">{mood.face} {mood.label} · {mood.hint}</div>
                <div className="text-[10px] text-neutral-500 mb-1 uppercase tracking-widest">XP</div>
                <div className="w-full bg-zinc-900 h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-red-500" style={{ width: `${xpPct}%` }} />
                </div>
                <div className="text-[10px] text-neutral-500 mt-1 tabular-nums">{pet.xp} / {pet.xp_cap}</div>
              </div>
            </div>

            {!pet.alive && (
              <div className="bg-red-500/10 border border-red-500/30 p-3 mb-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold mb-1">💀 Pet Has Burned</div>
                <div className="text-xs text-neutral-300">Visit /pet to perform the Rebirth Ritual.</div>
              </div>
            )}

            <FeedRow inventory={data.inventory} catalog={data.feed_items_catalog} reload={reload} alive={pet.alive} />

            <div className="mt-4 flex items-center gap-2">
              <Link
                to="/pet"
                data-testid="pet-widget-open-page"
                onClick={() => setOpen(false)}
                className="flex-1 text-center py-2 bg-[#D4AF37] hover:bg-yellow-400 text-black font-bold uppercase tracking-widest text-xs"
              >
                Visit Pet 🦅
              </Link>
              <button
                onClick={dismissForever}
                className="text-[10px] text-neutral-500 hover:text-white"
                title="Hide widget forever"
              >
                Hide
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FeedRow({ inventory, catalog, reload, alive }) {
  const [busy, setBusy] = useState(null);
  if (!catalog) return null;
  const feed = async (key) => {
    setBusy(key);
    try {
      const { data } = await (await import("axios")).default.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pet/feed`,
        { item_key: key },
      );
      const itemLabel = catalog[key]?.name || key;
      toast.success(
        data.hungry_penalty_applied
          ? `Fed ${itemLabel} +${data.xp_gained} XP (50% — pet was hungry)`
          : `Fed ${itemLabel} +${data.xp_gained} XP 🎉`
      );
      if (data.died) {
        toast.error(`💀 ${data.pet.name} burned out — go to /pet to rebirth!`, { duration: 8000 });
      }
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't feed.");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="grid grid-cols-3 gap-2">
      {Object.entries(catalog).map(([key, item]) => {
        const count = inventory?.[key] || 0;
        const disabled = !alive || count <= 0 || busy === key;
        return (
          <button
            key={key}
            data-testid={`pet-widget-feed-${key}`}
            onClick={() => feed(key)}
            disabled={disabled}
            className={`p-2 border-2 ${disabled ? "border-zinc-900 opacity-40" : "border-zinc-700 hover:border-[#D4AF37]"} flex flex-col items-center gap-0.5`}
            title={item.tagline}
          >
            <span className="text-xl">{item.icon}</span>
            <span className="text-[8px] uppercase tracking-widest text-neutral-400">×{count}</span>
            <span className="text-[8px] text-amber-400 font-bold">+{item.xp}</span>
          </button>
        );
      })}
    </div>
  );
}
